package com.essntl.features.task.domain.use_case

import com.essntl.core.utils.viewmodel.Operation
import com.essntl.core.utils.viewmodel.State
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import org.koin.core.annotation.Single

@Single
class AddTaskUseCase(
    private val taskRepository: TaskRepository,
    private val toastManager: ToastManager,
) {
    data class UseCaseState(
        val loadingSet: Set<String> = emptySet(),
    )

    private val _state = MutableStateFlow(UseCaseState())
    val state = _state.asStateFlow()

    private val _successEvent = MutableSharedFlow<Operation.Add<TaskModel>>()
    val successEvent = _successEvent.asSharedFlow()

    private val _failureEvent = MutableSharedFlow<Throwable>()
    val failureEvent = _failureEvent.asSharedFlow()

    suspend fun add(
        task: TaskModel,
        getState: () -> State<TaskModel> = { State.initial() },
        setState: (State<TaskModel>) -> Unit,
        onSuccess: suspend (TaskModel) -> Unit = {},
        onFailure: suspend (Throwable) -> Unit = {},
    ) {
        try {
            if (task.id in _state.value.loadingSet || getState().isLoading)
                return

            if (task.isEmpty)
                return

            setState(getState().copy(isLoading = true))

            _state.update {
                it.copy(
                    loadingSet = _state.value.loadingSet + task.id
                )
            }

            taskRepository.insert(task)
                .onSuccess { newTask ->
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - task.id
                        )
                    }

                    setState(State.success(newTask))

                    onSuccess(newTask)
                    _successEvent.emit(Operation.Add(newTask))

                    toastManager.success(description = "Task created successfully")
                }
                .onFailure { e ->
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - task.id
                        )
                    }

                    setState(State.failure(getState().data))

                    onFailure(e)
                    _failureEvent.emit(e)
                }
        } catch (e: CancellationException) {
            _state.update {
                it.copy(
                    loadingSet = it.loadingSet - task.id
                )
            }

            setState(State.failure(getState().data))

            onFailure(e)
            _failureEvent.emit(e)
        }
    }

}